"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const mobx_1 = require("mobx");
const video_client_core_1 = require("@video/video-client-core");
const react_1 = require("react");
const player_1 = require("../player");
const POLLING_INTERVAL = 20000;
const DEFAULT_PLAYER_OPTIONS = {
    muted: true,
    autoPlay: true,
    displayPoster: true,
    players: [{ id: "hlsjs" }, { id: "native-hls" }, { id: "mp4ws" }],
    preferredScoreLevel: video_client_core_1.types.SourceScoreLevel.High,
};
class MultistreamState extends video_client_core_1.ObservableEventEmitter {
    constructor(options) {
        super();
        this.streams = {};
        this.activePublicKeys = [];
        this.isInitialLoadDone = false;
        this.currentUnmutedStream = null;
        this.playersWrapperElement = (0, react_1.createRef)();
        this.manifestPollingInterval = null;
        this.playerOptions = {};
        const { publicKeys, maxPlayingStreams, manifestHost, videoClientOptions, playerOptions, muteOnFirstPlay } = options;
        this.publicKeys = publicKeys;
        this.manifestHost = manifestHost;
        this.maxPlayingStreams = maxPlayingStreams;
        this.videoClient = new video_client_core_1.VideoClient(videoClientOptions);
        this.playerOptions = playerOptions !== null && playerOptions !== void 0 ? playerOptions : DEFAULT_PLAYER_OPTIONS;
        this.muteOnFirstPlay = muteOnFirstPlay !== null && muteOnFirstPlay !== void 0 ? muteOnFirstPlay : false;
        (0, mobx_1.makeObservable)(this, {
            // observers
            publicKeys: mobx_1.observable,
            streams: mobx_1.observable,
            activePublicKeys: mobx_1.observable.ref,
            isInitialLoadDone: mobx_1.observable,
            // // actions
            setManifestUrls: mobx_1.action,
            startPlayer: mobx_1.action,
            stopPlayer: mobx_1.action,
            updatePublicKeys: mobx_1.action,
        });
        (0, mobx_1.reaction)(() => this.publicKeys, (newPublicKeys) => {
            this.emit("publicKeys", newPublicKeys);
            const publicKeysToPoll = newPublicKeys.filter((key) => !this.activePublicKeys.includes(key));
            this.setManifestUrls(publicKeysToPoll);
            if (this.manifestPollingInterval != null) {
                video_client_core_1.adapter.device.clearInterval(this.manifestPollingInterval);
            }
            this.manifestPollingInterval = video_client_core_1.adapter.device.setInterval(() => {
                this.setManifestUrls(publicKeysToPoll);
            }, POLLING_INTERVAL);
        });
        (0, mobx_1.reaction)(() => this.streams, () => {
            if (this.activePublicKeys.length === 0 && !this.isInitialLoadDone) {
                this.isInitialLoadDone = true;
                this.emit("isInitialLoadDone", true); // why isn't this auto-emitting?
            }
        });
        (0, mobx_1.reaction)(() => this.activePublicKeys, (newActivePublicKeys) => {
            this.emit("activePublicKeys", newActivePublicKeys);
            if (this.manifestPollingInterval != null)
                clearInterval(this.manifestPollingInterval);
            let keysToPoll;
            if (newActivePublicKeys.length > 0) {
                keysToPoll = this.publicKeys.filter((key) => !newActivePublicKeys.includes(key));
            }
            else {
                keysToPoll = this.publicKeys;
            }
            this.manifestPollingInterval = video_client_core_1.adapter.device.setInterval(() => {
                this.setManifestUrls(keysToPoll);
            }, POLLING_INTERVAL);
        });
        this.addInnerDisposer(() => {
            if (this.manifestPollingInterval != null)
                video_client_core_1.adapter.device.clearInterval(this.manifestPollingInterval);
            Object.values(this.streams).forEach(({ playerUi }) => {
                if (playerUi) {
                    playerUi.dispose();
                }
            });
            this.videoClient.dispose();
            this.activePublicKeys = [];
            this.publicKeys = [];
            this.streams = {};
        });
        this.setManifestUrls(publicKeys);
    }
    async setManifestUrls(publicKeys) {
        const newStreams = {};
        for (const publicKey of publicKeys) {
            const manifestUrl = `https://${this.manifestHost}/live/${publicKey}.json`;
            try {
                // eslint-disable-next-line no-await-in-loop
                const response = await this.fetchManifestAndSelectPoster({ manifestUrl, publicKey });
                if (response == null) {
                    newStreams[publicKey] = { manifestUrl, posterUrl: null, playerUi: null, disconnected: true };
                }
                else {
                    const { posterUrl, viewCount } = response;
                    newStreams[publicKey] = { manifestUrl, posterUrl, playerUi: null, disconnected: false, viewCount };
                }
            }
            catch (err) {
                // do nothing
            }
        }
        this.streams = { ...this.streams, ...newStreams };
    }
    startPlayer(publicKey) {
        var _a;
        const manifestUrl = (_a = this.streams[publicKey]) === null || _a === void 0 ? void 0 : _a.manifestUrl;
        if (!manifestUrl) {
            console.error("Error starting player. Invalid manifestUrl.");
            return;
        }
        const stream = this.streams[publicKey];
        if (stream == null) {
            console.error(`Stream with the public key ${publicKey} does not exist`);
            return;
        }
        if (this.activePublicKeys.includes(publicKey)) {
            return;
        }
        try {
            const player = this.videoClient.requestPlayer(manifestUrl, this.playerOptions);
            const playerUi = new player_1.PlayerUiState(player, { ...this.playerOptions, multistreamPlayerPublicKey: publicKey });
            if (this.activePublicKeys.length >= this.maxPlayingStreams) {
                this.stopPlayer(this.activePublicKeys[this.activePublicKeys.length - 1]);
            }
            if (!this.muteOnFirstPlay && this.activePublicKeys.length === 0) {
                playerUi.player.localAudioMuted = false;
                this.currentUnmutedStream = publicKey;
            }
            else {
                playerUi.player.localAudioMuted = true;
            }
            player.on("localAudioMuted", (muted) => {
                if (muted) {
                    return;
                }
                if (this.currentUnmutedStream == null) {
                    this.currentUnmutedStream = publicKey;
                    return;
                }
                if (this.currentUnmutedStream !== publicKey) {
                    const unmutedPlayerUi = this.streams[this.currentUnmutedStream].playerUi;
                    if (unmutedPlayerUi) {
                        unmutedPlayerUi.player.localAudioMuted = true;
                    }
                    this.currentUnmutedStream = publicKey;
                }
            });
            player.on("manifest", (manifest) => {
                const { viewCount } = manifest;
                this.streams = { ...this.streams, [publicKey]: { ...this.streams[publicKey], viewCount } };
                if (manifest.state === "offline") {
                    this.stopPlayer(publicKey);
                    this.streams = { ...this.streams, [publicKey]: { ...this.streams[publicKey], disconnected: true } };
                }
            });
            this.activePublicKeys = [...this.activePublicKeys, publicKey];
            this.streams = { ...this.streams, [publicKey]: { ...this.streams[publicKey], playerUi } };
        }
        catch (err) {
            console.error("Error initializing player.", err);
        }
    }
    stopPlayer(publicKey, removeFromStreams = false) {
        const stream = this.streams[publicKey];
        if (stream == null || stream.playerUi == null) {
            console.error(`Stream with the key ${publicKey} is not playing`);
            return;
        }
        this.activePublicKeys = this.activePublicKeys.filter((key) => key !== publicKey);
        if (this.currentUnmutedStream === publicKey) {
            this.currentUnmutedStream = null;
        }
        stream.playerUi.dispose();
        if (removeFromStreams) {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { [publicKey]: _notUsed, ...rest } = this.streams;
            this.streams = rest;
        }
        else {
            this.streams = { ...this.streams, [publicKey]: { ...this.streams[publicKey], playerUi: null } };
        }
    }
    async fetchManifestAndSelectPoster({ manifestUrl, publicKey, }) {
        var _a, _b, _c;
        try {
            const response = await fetch(manifestUrl);
            if (response.status !== 200) {
                this.emit("manifest-fetch-error", { response, publicKey });
                return null;
            }
            const responseJson = (await response.json());
            const { encodings } = (_a = responseJson.formats.jpeg) !== null && _a !== void 0 ? _a : {};
            if (encodings) {
                const sorted = encodings.sort((a, b) => b.videoHeight - a.videoHeight);
                // return poster with highest resolution
                return { posterUrl: (_c = (_b = sorted[0]) === null || _b === void 0 ? void 0 : _b.location) !== null && _c !== void 0 ? _c : null, viewCount: responseJson.viewCount };
            }
            return { posterUrl: null, viewCount: responseJson.viewCount };
        }
        catch (err) {
            console.error("Error fetching manifestUrl.", err);
            return null;
        }
    }
    updatePublicKeys(publicKeys) {
        if (this.manifestPollingInterval != null)
            clearInterval(this.manifestPollingInterval);
        const playersToStop = this.activePublicKeys.filter((key) => !publicKeys.includes(key));
        playersToStop.forEach((key) => this.stopPlayer(key, true));
        const newStreams = {};
        publicKeys.forEach((key) => {
            const stream = this.streams[key];
            if (stream != null) {
                newStreams[key] = stream;
            }
            else {
                newStreams[key] = { manifestUrl: "", posterUrl: null, playerUi: null, disconnected: true };
            }
        });
        this.streams = newStreams;
        this.publicKeys = publicKeys;
    }
}
MultistreamState.displayName = "MultistreamState";
exports.default = MultistreamState;
//# sourceMappingURL=ui-state.js.map